import {
    calculatorFishingSourcesEqual,
    parseCalculatorFishingSourceV1,
} from "./calculator-fishing-source.js";

function requireExactFields(value, expected, path) {
    const fields = Object.keys(value).sort();
    if (
        fields.length !== expected.length
        || fields.some((field, index) => field !== expected[index])
    ) {
        throw new TypeError(`${path} must contain exactly ${expected.join(" and ")}`);
    }
}

function requireGroupId(value, path) {
    if (typeof value !== "string" || !value.trim()) {
        throw new TypeError(`${path} must be a non-empty string`);
    }
    return value;
}

function requireItemId(value, path) {
    if (!Number.isSafeInteger(value) || value <= 0) {
        throw new TypeError(`${path} must be a positive safe integer`);
    }
    return value;
}

/**
 * Parses the selected subject of the loot provenance graph. `null` means that
 * no graph subject is selected; every non-null value is copied into one
 * canonical, immutable variant.
 */
export function parseCalculatorLootSubject(value, path = "calculator loot subject") {
    if (value === null) {
        return null;
    }
    if (typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(`${path} must be null or an object`);
    }
    if (value.kind === "group") {
        requireExactFields(value, ["groupId", "kind"], path);
        return Object.freeze({
            kind: "group",
            groupId: requireGroupId(value.groupId, `${path}.groupId`),
        });
    }
    if (value.kind === "row") {
        requireExactFields(value, ["groupId", "itemId", "kind"], path);
        return Object.freeze({
            kind: "row",
            groupId: requireGroupId(value.groupId, `${path}.groupId`),
            itemId: requireItemId(value.itemId, `${path}.itemId`),
        });
    }
    throw new TypeError(`${path}.kind must be group or row`);
}

export function calculatorLootSubjectsEqual(left, right) {
    const parsedLeft = parseCalculatorLootSubject(left, "left calculator loot subject");
    const parsedRight = parseCalculatorLootSubject(right, "right calculator loot subject");
    if (parsedLeft === null || parsedRight === null) {
        return parsedLeft === parsedRight;
    }
    return parsedLeft.kind === parsedRight.kind
        && parsedLeft.groupId === parsedRight.groupId
        && (
            parsedLeft.kind === "group"
            || (
                parsedRight.kind === "row"
                && parsedLeft.itemId === parsedRight.itemId
            )
        );
}

/**
 * Selects a channel by exact subject identity when the caller already owns the
 * source-scoped projection. Use `calculatorLootFactChannel` when a selected
 * source must also be enforced.
 */
export function calculatorLootSubjectFactChannel(factKey, subject) {
    const parsedSubject = parseCalculatorLootSubject(subject);
    if (parsedSubject === null) {
        return null;
    }
    if (factKey === null || typeof factKey !== "object" || Array.isArray(factKey)) {
        throw new TypeError("calculator loot provenance factKey must be an object");
    }

    if (parsedSubject.kind === "group") {
        if (factKey.groupId !== parsedSubject.groupId) {
            return null;
        }
        if (factKey.kind === "groupPresence") {
            return "presence";
        }
        return factKey.kind === "groupRate" ? "rate" : null;
    }

    if (factKey.itemId !== parsedSubject.itemId) {
        return null;
    }
    if (factKey.kind === "itemPresence") {
        return "presence";
    }
    if (factKey.kind === "itemQuantityDefault") {
        return "rate";
    }
    if (factKey.groupId !== parsedSubject.groupId) {
        return null;
    }
    if (factKey.kind === "groupMembership") {
        return "presence";
    }
    if (factKey.kind === "itemRate") {
        return "rate";
    }
    return factKey.kind === "itemQuantity" ? "rate" : null;
}

/**
 * Selects the graph channel for one already-validated provenance fact key.
 * Formula dependencies outside the exact selected source, group, or loot row
 * are deliberately excluded, even when they contributed to a broader Sankey
 * row.
 */
export function calculatorLootFactChannel(factKey, subject, fishingSource) {
    const source = parseCalculatorFishingSourceV1(
        fishingSource,
        "calculator loot subject fishingSource",
    );
    if (factKey === null || typeof factKey !== "object" || Array.isArray(factKey)) {
        throw new TypeError("calculator loot provenance factKey must be an object");
    }
    if (factKey.kind === "itemQuantityDefault") {
        return calculatorLootSubjectFactChannel(factKey, subject);
    }
    return calculatorFishingSourcesEqual(factKey.fishingSource, source)
        ? calculatorLootSubjectFactChannel(factKey, subject)
        : null;
}
